/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useCallback, useState, type ReactNode } from "react";

export type Density = "comfortable" | "compact";

const DENSITY_STORAGE_KEY = "table-density";

function readDensity(): Density {
  try {
    const raw = localStorage.getItem(DENSITY_STORAGE_KEY);
    return raw === "compact" ? "compact" : "comfortable";
  } catch {
    return "comfortable";
  }
}

function writeDensity(density: Density): void {
  try {
    localStorage.setItem(DENSITY_STORAGE_KEY, density);
  } catch {
    // localStorage can throw in restrictive environments (e.g. Safari private browsing) — the
    // preference just won't persist across reloads, which is a fine degradation for this feature.
  }
}

interface DensityContextValue {
  density: Density;
  toggleDensity: () => void;
  /** Cell padding classes for the active density — spread directly onto a className. */
  cellPad: string;
  /** Row height class for the active density. */
  rowH: string;
}

const DEFAULT_DENSITY_VALUE: DensityContextValue = {
  density: "comfortable",
  toggleDensity: () => {},
  cellPad: "py-2.5 px-4",
  rowH: "h-11",
};

// Defaults to "comfortable" rather than requiring a Provider — `table.tsx` (a shared, leaf-level
// primitive) calls useDensity() internally, and many existing component tests render it without
// mounting the full app provider tree.
const DensityContext = createContext<DensityContextValue>(DEFAULT_DENSITY_VALUE);

export const DensityProvider = ({ children }: { children: ReactNode }) => {
  const [density, setDensity] = useState<Density>(readDensity);

  const toggleDensity = useCallback(() => {
    setDensity((prev) => {
      const next: Density = prev === "compact" ? "comfortable" : "compact";
      writeDensity(next);
      return next;
    });
  }, []);

  const cellPad = density === "compact" ? "py-1.5 px-3" : "py-2.5 px-4";
  const rowH = density === "compact" ? "h-8" : "h-11";

  return (
    <DensityContext.Provider value={{ density, toggleDensity, cellPad, rowH }}>
      {children}
    </DensityContext.Provider>
  );
};

export const useDensity = (): DensityContextValue => useContext(DensityContext);
